/**
 * GuideService — catalogue des guides video contextuels.
 *
 * - Charge ``/guides/manifest.json`` en SAME-ORIGIN (volume nginx, no-store) :
 *   aucune dependance cross-origin (regle frontend-resilience).
 * - Fait correspondre la ROUTE COURANTE aux guides (prefixes ``routes`` du
 *   manifest) et expose le guide actif sous forme de signaux.
 * - Choisit la VARIANTE LANGUE (video + sous-titres + poster) selon la locale
 *   active de l'utilisateur (Transloco / localStorage ``vd-locale``),
 *   repli ``fr``.
 * - Memorise vu / masque par guide dans localStorage (pas de spam).
 */

import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

export interface GuideChapter {
  id: string;
  title: string;
  start: number;
  duration: number;
}

export interface GuideLocaleVariant {
  title: string;
  description: string;
  video: string;
  vtt: string;
  srt: string;
  poster: string;
  duration_sec: number;
  chapters: GuideChapter[];
}

export interface GuideEntry {
  id: string;
  order: number;
  routes: string[];
  roles?: string[] | null;
  locales: Record<string, GuideLocaleVariant>;
}

const SEEN_KEY = (id: string) => `vd-guide-seen:${id}`;
const DISMISS_KEY = (id: string) => `vd-guide-dismissed:${id}`;

@Injectable({ providedIn: 'root' })
export class GuideService {
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  readonly guides = signal<GuideEntry[]>([]);
  readonly currentUrl = signal<string>('/');
  readonly locale = signal<string>('fr');
  /** Version localStorage (vu/masque) — incrementee pour recalculer. */
  private readonly storageRev = signal(0);

  constructor() {
    this.locale.set(this.readLocale());
    this.transloco.langChanges$.subscribe((l) => this.locale.set(this.normalize(l)));
    this.currentUrl.set(this.router.url || '/');
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
    this.load();
  }

  private readLocale(): string {
    try {
      return this.normalize(localStorage.getItem('vd-locale') || this.transloco.getActiveLang());
    } catch {
      return 'fr';
    }
  }

  private normalize(l: string | null | undefined): string {
    const v = (l || 'fr').trim();
    if (v.toLowerCase().startsWith('ar')) return 'ar-TN';
    if (v.toLowerCase().startsWith('en')) return 'en';
    return 'fr';
  }

  private load(): void {
    // fetch natif : pas d'interceptor auth necessaire pour un statique public.
    fetch('/guides/manifest.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.guides?.length) this.guides.set(data.guides as GuideEntry[]);
      })
      .catch(() => void 0); // fail-soft : pas de guide, pas de bulle.
  }

  /** Guide correspondant a la route courante (le plus specifique gagne). */
  readonly activeGuide = computed<GuideEntry | null>(() => {
    const url = (this.currentUrl() || '/').split('?')[0];
    let best: GuideEntry | null = null;
    let bestLen = -1;
    for (const g of this.guides()) {
      for (const r of g.routes || []) {
        const match = url === r || url.startsWith(r.endsWith('/') ? r : r + '/');
        if ((match || url === r) && r.length > bestLen) {
          best = g;
          bestLen = r.length;
        }
      }
    }
    return best;
  });

  /** Variante dans la langue de l'utilisateur (repli fr puis 1re dispo). */
  variantOf(g: GuideEntry | null): GuideLocaleVariant | null {
    if (!g) return null;
    return g.locales[this.locale()] || g.locales['fr']
      || Object.values(g.locales)[0] || null;
  }

  readonly activeVariant = computed<GuideLocaleVariant | null>(() => {
    this.storageRev();
    return this.variantOf(this.activeGuide());
  });

  readonly activeDismissed = computed<boolean>(() => {
    this.storageRev();
    const g = this.activeGuide();
    if (!g) return true;
    try {
      return localStorage.getItem(DISMISS_KEY(g.id)) === '1';
    } catch {
      return false;
    }
  });

  readonly activeSeen = computed<boolean>(() => {
    this.storageRev();
    const g = this.activeGuide();
    if (!g) return false;
    try {
      return localStorage.getItem(SEEN_KEY(g.id)) === '1';
    } catch {
      return false;
    }
  });

  markSeen(id: string): void {
    try { localStorage.setItem(SEEN_KEY(id), '1'); } catch { /* quota */ }
    this.storageRev.update((v) => v + 1);
  }

  dismiss(id: string): void {
    try { localStorage.setItem(DISMISS_KEY(id), '1'); } catch { /* quota */ }
    this.storageRev.update((v) => v + 1);
  }

  restore(id: string): void {
    try { localStorage.removeItem(DISMISS_KEY(id)); } catch { /* quota */ }
    this.storageRev.update((v) => v + 1);
  }

  isRtl(): boolean {
    return this.locale() === 'ar-TN';
  }
}
